import type { PrivacyConsent } from '@/types/share'
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import type {
  SharedImageDetail,
  CommunityResponse,
  ShareResponse,
  MySharedResponse,
} from '@/types/share'

/** 이미지 공유하기 */
export function useShareImage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: { generationId: string; s3Key: string } & PrivacyConsent) => {
      return api.post<ShareResponse>('/share', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mySharedImages'] })
      queryClient.invalidateQueries({ queryKey: ['community'] })
    },
  })
}

/** 공유 취소하기 */
export function useUnshareImage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (shareId: string) => {
      return api.delete<{ success: boolean }>(`/share/${shareId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mySharedImages'] })
      queryClient.invalidateQueries({ queryKey: ['community'] })
    },
  })
}

/** 내가 공유한 이미지 목록 조회 (s3Key → shareId 매핑) */
export function useMySharedImages() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const userId = useAuthStore((state) => state.user?.id)

  return useQuery({
    queryKey: ['mySharedImages', userId],
    queryFn: async () => {
      const data = await api.get<MySharedResponse>('/share/my')
      // s3Key → shareId 매핑으로 변환
      const map = new Map<string, string>()
      for (const item of data.items) {
        map.set(item.s3Key, item.shareId)
      }
      return map
    },
    enabled: isAuthenticated && !!userId,
    staleTime: 1000 * 60, // 1분
  })
}

/** 공유 이미지 상세 조회 (Public) */
export function useSharedImage(shareId: string | null) {
  return useQuery({
    queryKey: ['sharedImage', shareId],
    queryFn: () => api.get<SharedImageDetail>(`/share/${shareId}`),
    enabled: !!shareId,
    staleTime: 1000 * 60 * 5, // 5분
  })
}

/** 커뮤니티 이미지 목록 (무한 스크롤) */
export function useInfiniteCommunity(limit: number = 20) {
  return useInfiniteQuery({
    queryKey: ['community', 'infinite', limit],
    queryFn: ({ pageParam }) =>
      api.get<CommunityResponse>(`/community?page=${pageParam}&limit=${limit}`),
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.page + 1 : undefined,
    initialPageParam: 1,
    staleTime: 1000 * 60, // 1분
  })
}

/** 공유 무효화 (히스토리 삭제 시 등) */
export function useInvalidateShare() {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: ['mySharedImages'] })
    queryClient.invalidateQueries({ queryKey: ['community'] })
  }
}
